<template>
  <q-page class="acf-page">
    <div class="acf-container">
      <!-- Header -->
      <div class="acf-header">
        <div class="acf-breadcrumb">
          <router-link to="/admin" class="acf-crumb">Admin</router-link>
          <span class="acf-crumb-sep">/</span>
          <router-link to="/admin/courses" class="acf-crumb">Cursos</router-link>
          <span class="acf-crumb-sep">/</span>
          <span class="acf-crumb acf-crumb--active">Nuevo curso</span>
        </div>
        <h1 class="acf-title">Crear curso</h1>
      </div>

      <!-- Form -->
      <q-form class="acf-form" @submit.prevent="handleSubmit">
        <!-- Titulo -->
        <q-input
          v-model="form.title"
          outlined
          label="Título"
          :rules="[(v: string) => !!v || 'El título es obligatorio']"
          maxlength="255"
          class="acf-field"
          @update:model-value="onTitleInput"
        />

        <!-- Slug -->
        <q-input
          v-model="form.slug"
          outlined
          label="Slug"
          hint="Identificador único en la URL (se genera automáticamente)"
          :rules="[
            (v: string) => !!v || 'El slug es obligatorio',
            (v: string) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(v) || 'Solo minúsculas, números y guiones',
          ]"
          maxlength="255"
          class="acf-field"
          @update:model-value="onSlugInput"
        />

        <!-- Descripcion corta -->
        <q-input
          v-model="form.shortDescription"
          outlined
          label="Descripción corta"
          type="textarea"
          :rows="2"
          maxlength="500"
          class="acf-field"
        />

        <!-- Descripcion completa -->
        <q-input
          v-model="form.description"
          outlined
          label="Descripción completa"
          type="textarea"
          :rows="4"
          class="acf-field"
        />

        <!-- Estado -->
        <div class="acf-field">
          <p class="acf-label">Estado</p>
          <q-btn-toggle
            v-model="form.status"
            no-caps
            unelevated
            :options="[
              { label: 'Borrador', value: 'draft' },
              { label: 'Publicado', value: 'published' },
            ]"
            :toggle-color="form.status === 'published' ? 'positive' : 'warning'"
            color="grey-3"
            text-color="grey-8"
          />
        </div>

        <!-- URL de imagen de portada -->
        <q-input
          v-model="form.coverImageUrl"
          outlined
          label="URL de imagen de portada"
          class="acf-field"
        />

        <!-- Cover preview -->
        <div v-if="form.coverImageUrl" class="acf-cover-preview">
          <img :src="form.coverImageUrl" alt="Preview de portada" class="acf-cover-img" />
        </div>

        <!-- Actions -->
        <div class="acf-actions">
          <q-btn
            flat
            no-caps
            label="Cancelar"
            color="grey-7"
            to="/admin/courses"
          />
          <q-btn
            unelevated
            no-caps
            type="submit"
            color="accent"
            icon="save"
            label="Guardar curso"
            :loading="saving"
          />
        </div>
      </q-form>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { useAdminCoursesStore } from 'stores/useAdminCoursesStore';

const router = useRouter();
const $q = useQuasar();
const adminStore = useAdminCoursesStore();

// ---------- State ----------
const saving = ref(false);
const slugTouched = ref(false);

const form = ref({
  title: '',
  slug: '',
  shortDescription: '',
  description: '',
  coverImageUrl: '',
  status: 'draft' as 'draft' | 'published',
});

// ---------- Slug auto-derive ----------
function deriveSlug(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip diacritics
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

function onTitleInput(value: string | number | null) {
  if (!slugTouched.value) {
    form.value.slug = deriveSlug(String(value ?? ''));
  }
}

function onSlugInput() {
  slugTouched.value = true;
}

// ---------- Submit ----------
async function handleSubmit() {
  saving.value = true;
  try {
    const body = {
      title: form.value.title,
      slug: form.value.slug,
      ...(form.value.shortDescription ? { shortDescription: form.value.shortDescription } : {}),
      ...(form.value.description ? { description: form.value.description } : {}),
      ...(form.value.coverImageUrl ? { coverImageUrl: form.value.coverImageUrl } : {}),
      status: form.value.status,
    };

    const { id } = await adminStore.createCourse(body);
    $q.notify({ type: 'positive', message: 'Curso creado correctamente' });
    await router.push({ name: 'admin-course-edit', params: { id } });
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 409) {
      $q.notify({ type: 'negative', message: 'El slug ya está en uso' });
    } else {
      $q.notify({ type: 'negative', message: 'No se pudo guardar. Revisá tu conexión e intentalo de nuevo.' });
    }
  } finally {
    saving.value = false;
  }
}
</script>

<style scoped>
.acf-page {
  padding: 24px;
  background: var(--color-seafoam, #e8f4f8);
}

.acf-container {
  max-width: 720px;
  margin: 0 auto;
}

.acf-header {
  margin-bottom: 24px;
}

.acf-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--oa-text-muted);
  margin-bottom: 8px;
}

.acf-crumb {
  color: var(--oa-text-muted);
  text-decoration: none;
  transition: color 0.15s;
}

.acf-crumb:hover {
  color: var(--oa-text-primary);
}

.acf-crumb--active {
  color: var(--oa-text-primary);
}

.acf-crumb-sep {
  color: var(--oa-text-disabled);
}

.acf-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.acf-form {
  background: var(--color-sand-light, #f5efe6);
  border: 1px solid var(--oa-border);
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.acf-field {
  margin-bottom: 8px;
}

.acf-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--oa-text-secondary);
  margin: 0 0 8px;
}

.acf-cover-preview {
  margin-bottom: 8px;
  border-radius: 8px;
  overflow: hidden;
  max-height: 200px;
}

.acf-cover-img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}

.acf-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--oa-border);
}
</style>
